"use client";

import { useEffect, useState } from "react";
import { useQueryState, parseAsStringEnum, parseAsString } from "nuqs";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";

import {
  createPasswordResetSchema,
  type PasswordResetFormValues,
  passwordResetSchema,
} from "@/shared/lib/validation/user-schemas";
import { EmailVerificationWaiting } from "@/shared/ui/email-verification-waiting";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/ui/alert-dialog";
import { useRouter } from "next/navigation";
import { userResetPassword } from "@/server-action/user/user-reset-password";
import { SuccessStep } from "@/shared/ui/success-step";
import { PasswordResetStep } from "../model/type";
import { EmailStep } from "./email-step";
import { PasswordStep } from "./password-step";
import { useTranslations } from "next-intl";

export function PasswordResetForm() {
  const router = useRouter();
  const t = useTranslations();

  const schema = createPasswordResetSchema(t);

  const [step, setStep] = useQueryState("step", parseAsStringEnum(Object.values(PasswordResetStep)));
  const [verificationCode, setVerificationCode] = useQueryState("verificationCode", parseAsString);
  const [emailFromUrl, setEmailFromUrl] = useQueryState("email", parseAsString);
  const [confirmPassword, setConfirmPassword] = useState("");

  const [errorFromUrl, setErrorFromUrl] = useQueryState("error", parseAsString);
  const [errorDialog, setErrorDialog] = useState<{
    show: boolean;
    title: string;
    description: string;
  }>({
    show: false,
    title: "",
    description: "",
  });

  const getErrorMessage = (errorType: string) => {
    const messages = {
      expired: { title: "이메일 인증 시간이 만료되었어요.", description: "인증 메일을 다시 보내주세요." },
      "already-verified": { title: "이미 인증되었어요.", description: "이미 인증된 이메일이에요." },
      invalid: { title: "유효하지 않은 코드에요.", description: "인증 메일을 다시 보내주세요." },
    };
    return messages[errorType as keyof typeof messages];
  };

  const form = useForm<PasswordResetFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: "",
      verificationCode: verificationCode || "",
      newPassword: "",
    },
  });

  // 새로고침 시 처음으로 초기화
  useEffect(() => {
    const navigationEntries = performance.getEntriesByType("navigation") as PerformanceNavigationTiming[];
    const isRefresh = navigationEntries.length > 0 && navigationEntries[0].type === "reload";

    if (isRefresh) {
      setStep(null);
      setVerificationCode(null);
      setEmailFromUrl(null);
    }
  }, [setStep, setVerificationCode, setEmailFromUrl]);

  // URL에서 이메일 정보가 있으면 폼에 설정
  useEffect(() => {
    if (emailFromUrl && emailFromUrl !== form.getValues("email")) {
      form.setValue("email", emailFromUrl);
    }
  }, [emailFromUrl, form]);

  // 이메일 없으면 waiting 단계 접근 불가
  useEffect(() => {
    if (step === "waiting" && !form.getValues("email")) {
      setStep(null);
    }
  }, [step, form, setStep]);

  const onClickSendEmail = () => {
    setStep(PasswordResetStep.WAITING);
  };

  const onClickPasswordSubmit = async (data: PasswordResetFormValues) => {
    // 패스워드 단계가 아니면 제출하지 않음
    if (step !== PasswordResetStep.PASSWORD) {
      return;
    }

    // 비밀번호 확인 검증
    const newPassword = data.newPassword;
    if (!newPassword || !confirmPassword) {
      return;
    }

    if (newPassword !== confirmPassword) {
      return;
    }

    try {
      const result = await userResetPassword(data.email, data.verificationCode as string, data.newPassword);
      if (!result.ok) {
        throw new Error(result.error.message);
      }
      setStep(PasswordResetStep.SUCCESS);
    } catch (error: unknown) {
      console.error("❌ Password reset failed:", error);

      if (error instanceof Error && error.message === "Invalid or expired verification code") {
        setErrorDialog({
          show: true,
          title: "이메일 인증 시간이 만료되었어요.",
          description: "인증 메일을 다시 보내주세요.",
        });
      } else {
        setErrorDialog({
          show: true,
          title: "비밀번호 변경 오류",
          description: "비밀번호 변경 중 오류가 발생했습니다.\n다시 시도해주세요.",
        });
      }
    }
  };

  // 리다이렉트 시 이메일 인증 에러 처리
  useEffect(() => {
    if (errorFromUrl) {
      const errorMessage = getErrorMessage(errorFromUrl);
      if (errorMessage) {
        setErrorDialog({ show: true, ...errorMessage });
        setErrorFromUrl(null);
      }
    }
  }, [errorFromUrl, setErrorFromUrl]);

  const renderStep = () => {
    if (!step) return <EmailStep onClickSendEmail={onClickSendEmail} />;
    if (step === "waiting") return <EmailVerificationWaiting email={form.getValues("email")} />;
    if (step === "password")
      return <PasswordStep confirmPassword={confirmPassword} setConfirmPassword={setConfirmPassword} />;
    if (step === "success")
      return (
        <SuccessStep
          title={t("f_pe_title")}
          button1={{ label: "f_pe_btn_main", onClick: () => router.push("/") }}
          button2={{ label: "f_pe_btn_login", onClick: () => router.push("/login") }}
        />
      );
  };

  return (
    <>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onClickPasswordSubmit)}>
          <div
            className={`pc:px-5 tb:px-10 pc:h-[calc(100vh-284px)] tb:h-[calc(100vh-406px)] pc:mt-0 tb:mt-20 mt-12 flex h-[calc(100vh-374px)] flex-col items-center justify-center bg-white px-4`}
          >
            <div className="flex w-full justify-center overflow-y-auto">{renderStep()}</div>
          </div>
        </form>
      </FormProvider>

      <AlertDialog open={errorDialog.show} onOpenChange={(open) => setErrorDialog((prev) => ({ ...prev, show: open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{errorDialog.title}</AlertDialogTitle>
            <AlertDialogDescription className="whitespace-pre-line">{errorDialog.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => {
                setVerificationCode(null);
                setEmailFromUrl(null);
                setErrorDialog({ show: false, title: "", description: "" });
                setStep(null);
                form.reset();
              }}
            >
              확인
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
